import React, { lazy, Suspense } from 'react';
import { observer } from 'mobx-react';
import { Route, Redirect, Switch, Router } from 'react-router-dom';
import { useStore } from '../store/mst';
import { useTracking } from 'react-tracking';
import history from './history';
import { syncHistoryWithStore } from '../store/mst/Router/utils';
import AnalyticsEvents from '../analytics/analyticsEvents';
import ProtectedRoute from './ProtectedRoute';
import ErrorOverlay from '../components/errors/ErrorOverlay';
import * as RoutePaths from './paths';
import { routes } from './routerConfig';
import App from './App';
import { AnalyticsEventsProps } from '../analytics/analyticsEventsProps';
import RouteLoadingFallback from './components/RouteLoadingFallback';

const lazyRoutes = routes.map((route) => ({
  ...route,
  component: lazy(route.component),
}));

const AppRouter: React.FunctionComponent = () => {
  const store = useStore();
  const isLoggedIn = !!store.account.user;
  const { Track } = useTracking({}, {
    dispatch: (eventName: { event: AnalyticsEvents, properties: AnalyticsEventsProps.EventProps }) => {
      store.account.trackUserForAnalytics(eventName.event, { ...eventName.properties });
    },
  });

  const syncedHistory = syncHistoryWithStore(history, store.router);

  return (
    <Router history={syncedHistory}>
      <Track>
        <Suspense fallback={<RouteLoadingFallback fullViewport />}>
          <Switch>
            {lazyRoutes.map(({ path, component, protected: isProtected, unauthenticate }) => (
              <Route
                key={Array.isArray(path) ? path.join(',') : path}
                exact
                path={path}
                render={(props) => {
                  if (unauthenticate && isLoggedIn) {
                    store.auth.unauthenticate();
                  }
                  if (isProtected && !isLoggedIn) {
                    return <Redirect to={RoutePaths.loginPath} />;
                  }
                  return React.createElement(component, props);
                }}
              />
            ))}
            <ErrorOverlay>
              <ProtectedRoute component={App} />
            </ErrorOverlay>
          </Switch>
        </Suspense>
      </Track>
    </Router>
  );
};

export default observer(AppRouter);
